import { AlertTriangle, ArchiveX, LoaderCircle, RefreshCw, Search } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { formatDate } from '../utils/format'

export function Button({ children, className = '', variant = 'primary', loading = false, disabled = false, onClick, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'quiet' | 'danger'; loading?: boolean }) {
  const [activated, setActivated] = useState(false)
  const feedbackTimer = useRef<number | null>(null)
  useEffect(() => () => {
    if (feedbackTimer.current !== null) window.clearTimeout(feedbackTimer.current)
  }, [])

  return (
    <button
      className={`button button--${variant} ${activated ? 'button--activated' : ''} ${className}`}
      disabled={loading || disabled}
      aria-busy={loading || undefined}
      data-state={loading ? 'loading' : disabled ? 'disabled' : activated ? 'activated' : 'idle'}
      onClick={(event) => {
        setActivated(true)
        if (feedbackTimer.current !== null) window.clearTimeout(feedbackTimer.current)
        feedbackTimer.current = window.setTimeout(() => setActivated(false), 420)
        onClick?.(event)
      }}
      {...props}
    >
      {loading && <LoaderCircle className="spin" size={17} aria-hidden="true" />}
      {children}
    </button>
  )
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' }) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}

export function statusTone(status?: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  if (['CONFIRMED', 'RESOLVED', 'ACTIVE', 'APPROVED', 'GENERATED'].includes(status || '')) return 'success'
  if (['REVIEW_REQUIRED', 'OPEN', 'WARNING', 'auth-required', 'retry', 'queued'].includes(status || '')) return 'warning'
  if (['FAILED', 'BLOCKER', 'conflict', 'failed'].includes(status || '')) return 'danger'
  if (['QUEUED', 'PROCESSING', 'READY_TO_CONFIRM', 'syncing'].includes(status || '')) return 'info'
  return 'neutral'
}

export function CachedNotice({ fetchedAt, stale }: { fetchedAt: number; stale?: boolean }) {
  return (
    <div className="inline-notice inline-notice--cached" role="status">
      <ArchiveX size={18} aria-hidden="true" />
      <span>{stale ? 'Показана сохранённая копия' : 'Данные доступны без сети'} · сохранено {formatDate(fetchedAt, true)}</span>
    </div>
  )
}

export function ErrorState({ error, onRetry, compact = false }: { error: Error; onRetry?: () => void; compact?: boolean }) {
  return (
    <div className={compact ? 'error-state error-state--compact' : 'error-state'} role="alert">
      <AlertTriangle size={compact ? 20 : 28} aria-hidden="true" />
      <div>
        <strong>Не удалось показать данные</strong>
        <p>{error.message}</p>
      </div>
      {onRetry && <Button variant="secondary" onClick={onRetry}><RefreshCw size={16} />Повторить</Button>}
    </div>
  )
}

export function EmptyState({ title, description, action, icon }: { title: string; description: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="empty-state">
      <div className="empty-state__icon" aria-hidden="true">{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  )
}

export function LoadingState({ label = 'Загружаем данные' }: { label?: string }) {
  return <div className="loading-state" role="status"><LoaderCircle className="spin" size={23} /><span>{label}</span></div>
}

export function SearchField({ value, onChange, placeholder, label = 'Поиск' }: { value: string; onChange(value: string): void; placeholder: string; label?: string }) {
  const id = useId()
  return (
    <label className="search-field" htmlFor={id}>
      <span className="sr-only">{label}</span>
      <Search size={18} aria-hidden="true" />
      <input id={id} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} type="search" />
    </label>
  )
}

export function Modal({
  title,
  description,
  open,
  onClose,
  children,
  size = 'medium',
  dismissible = true,
  className = '',
}: {
  title: string
  description?: string
  open: boolean
  onClose(): void
  children: ReactNode
  size?: 'small' | 'medium' | 'large'
  dismissible?: boolean
  className?: string
}) {
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])
  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const preferred = dialogRef.current?.querySelector<HTMLElement>('[data-autofocus], [autofocus]')
    const fallback = dialogRef.current?.querySelector<HTMLElement>('input:not([type="hidden"]), select, textarea, button:not(.modal__close), a[href]')
    ;(preferred || fallback)?.focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) onCloseRef.current()
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((element) => !element.hasAttribute('disabled'))
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; previous?.focus() }
  }, [dismissible, open])
  if (!open) return null
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(event) => { if (dismissible && event.currentTarget === event.target) onClose() }}>
      <div className={`modal modal--${size} ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} ref={dialogRef}>
        <div className="modal__header">{dismissible && <button className="modal__close" onClick={onClose} aria-label="Закрыть">×</button>}<div><h2 id={titleId}>{title}</h2>{description && <p id={descriptionId}>{description}</p>}</div></div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Отмена',
  tone = 'primary',
  loading = false,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  description: string
  confirmLabel: string
  cancelLabel?: string
  tone?: 'primary' | 'danger'
  loading?: boolean
  onConfirm(): void
  onClose(): void
}) {
  return (
    <Modal open={open} onClose={onClose} title={title} description={description} size="small" dismissible={!loading}>
      <div className="modal__footer">
        <Button type="button" variant="quiet" onClick={onClose} disabled={loading}>{cancelLabel}</Button>
        <Button type="button" variant={tone} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
      </div>
    </Modal>
  )
}

export function Pagination({ page, limit, total, onChange }: { page: number; limit: number; total: number; onChange(page: number): void }) {
  const pages = Math.max(1, Math.ceil(total / limit))
  if (pages <= 1) return null
  return (
    <nav className="pagination" aria-label="Страницы">
      <Button variant="secondary" disabled={page <= 1} onClick={() => onChange(page - 1)}>Назад</Button>
      <span>Страница {page} из {pages}</span>
      <Button variant="secondary" disabled={page >= pages} onClick={() => onChange(page + 1)}>Далее</Button>
    </nav>
  )
}
